import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Toolbar, 
  Divider, 
  ListItemButton,
  useTheme,
  useMediaQuery,
  Box
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import BarChartIcon from '@mui/icons-material/BarChart';
import EventIcon from '@mui/icons-material/Event';
import CategoryIcon from '@mui/icons-material/Category';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import QrCodeIcon from '@mui/icons-material/QrCode';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import StoreIcon from '@mui/icons-material/Store';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { UserRole } from '../../types';

const drawerWidth = 220;

interface SidebarProps {
  mobileOpen: boolean;
  onDrawerToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onDrawerToggle }) => {
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleNavigation = () => {
    if (isMobile) {
      onDrawerToggle();
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', roles: [UserRole.DIRECTOR, UserRole.MANAGER, UserRole.CLEANER] },
    { text: 'Tareas', icon: <AssignmentIcon />, path: '/tasks', roles: [UserRole.DIRECTOR, UserRole.MANAGER, UserRole.CLEANER] },
    { text: 'Abonos', icon: <RoomServiceIcon />, path: '/services', roles: [UserRole.DIRECTOR, UserRole.MANAGER, UserRole.CLEANER] },
    // { text: 'Servicios Programados', icon: <EventIcon />, path: '/scheduled-services', roles: [UserRole.DIRECTOR, UserRole.MANAGER] },
    { text: 'Gestión de QR', icon: <QrCodeIcon />, path: '/qr-codes', roles: [UserRole.DIRECTOR] },
    { text: 'Escanear QR', icon: <QrCodeScannerIcon />, path: '/scan-qr', roles: [UserRole.DIRECTOR, UserRole.MANAGER, UserRole.CLEANER] },
    { text: 'Empresas', icon: <BusinessIcon />, path: '/companies', roles: [UserRole.DIRECTOR] },
    { text: 'Particulares', icon: <AccountCircleIcon />, path: '/individuals', roles: [UserRole.DIRECTOR] },
    { text: 'Sucursales', icon: <StoreIcon />, path: '/branches', roles: [UserRole.DIRECTOR] },
    { text: 'Asignar Usuarios', icon: <GroupAddIcon />, path: '/branches/assign-users', roles: [UserRole.DIRECTOR] },
    { text: 'Comisiones', icon: <AttachMoneyIcon />, path: '/commissions', roles: [UserRole.DIRECTOR] },
    { text: 'Usuarios', icon: <GroupIcon />, path: '/users', roles: [UserRole.DIRECTOR] },
    { text: 'Reportes', icon: <BarChartIcon />, path: '/reports', roles: [UserRole.DIRECTOR] },
    { text: 'Categorías', icon: <CategoryIcon />, path: '/categories', roles: [UserRole.DIRECTOR] },
    { text: 'Perfil', icon: <PersonIcon />, path: '/profile', roles: [UserRole.DIRECTOR, UserRole.MANAGER, UserRole.CLEANER] }
  ];

  const drawer = (
    <>
      <Toolbar />
      <Divider />
      <List>
        {menuItems
          .filter(item => user && item.roles.includes(user.role))
          .map((item) => (
          <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
            <ListItemButton 
              component={Link} 
              to={item.path} 
              selected={location.pathname === item.path}
              onClick={handleNavigation}
              sx={{
                minHeight: 48,
                justifyContent: 'initial',
                px: 2.5,
                borderRadius: '8px',
                margin: '4px 8px',
                transition: 'background-color 0.3s, transform 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  transform: 'scale(1.02)'
                },
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                    color: 'white',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ 
                minWidth: 0,
                mr: 3,
                justifyContent: 'center',
                color: theme.palette.primary.dark,
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
    >
      {/* Drawer temporal para móviles */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth 
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Drawer permanente para desktop */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth 
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar; 